import {
  PORTFOLIO_COUNT_UNKNOWN,
  type PortfolioEstimate,
  type PortfolioInventory,
  type PortfolioPassRequest,
  type PortfolioTriage,
} from "@/services/migrations/portfolio/types";
import { portfolioDisabledRefusal, portfolioRefusal } from "@/services/migrations/portfolio";
import { humanize, numberOrNull } from "@/services/studio/primitives";
import type { CoverageTone } from "./coverage";

/**
 * Presentation rules for the portfolio pass, kept out of the components so the two
 * decisions that matter can be tested directly rather than inferred from a rendered
 * attribute.
 *
 * DECISION ONE — WHETHER THE PAID ACTION MAY BE OFFERED.
 *
 * This is NOT a security control and must never be mistaken for one. The server enforces
 * entitlement on all four paid routes and refuses with a 403 regardless of what this file
 * decides. `resolvePortfolioGate` exists for a different job: making the refusal a NEXT
 * STEP rather than a dead end, and making it look the same whether it arrived with the
 * free inventory (before any call) or as a 403 (after one). Those two paths converging on
 * one `PortfolioGate` is the whole design — a re-enabled button, a stale tab, or a direct
 * call all land on the identical "here is who to contact", because the 403 body and the
 * inventory's `triage` block are the same shape and both come through here.
 *
 * The gate is CLOSED by default. An absent triage block, an unreadable one, or a `triage`
 * this client cannot parse all resolve to unavailable. Failing the other way would offer
 * a paid run on the strength of a parse error.
 *
 * DECISION TWO — WHAT THE HEADLINE NUMBER IS ALLOWED TO BE.
 *
 * `counts.workflow_rules` is the total. `counts.addressable` is a SUBSET: the rules a
 * pass could actually be built over. They differ by `unaddressable`, and `addressable` is
 * always the better-looking of the two. So the headline is pinned to `workflow_rules`,
 * `unaddressable` renders BESIDE it at every value including zero (the same rule the
 * engine applies to `undeclared`), and `complete: false` is a caveat on the face of the
 * panel rather than something behind a disclosure. A count that never arrived reads as
 * `?`, never as `0`.
 */

/** Said when the server sent no `contact`. The refusal must always name a next step. */
export const PORTFOLIO_CONTACT_FALLBACK =
  "contact your nCoder account team to enable the portfolio pass for this org";

/** Said when the server sent no `message`. Describes a product boundary, not a fault. */
export const PORTFOLIO_GATE_FALLBACK_MESSAGE =
  "The portfolio pass is not enabled for this org.";

/**
 * Said when THIS DEPLOYMENT runs no passes and the server sent no sentence of its own.
 *
 * Names an operator and a flag, and names no account team, because `PORTFOLIO_DISABLED`
 * is a deployment state and not a commercial one.
 */
export const PORTFOLIO_DISABLED_FALLBACK_MESSAGE =
  "This deployment does not run portfolio passes.";

export interface PortfolioGate {
  /** May the paid action be offered? `false` disables the control, not just its tone. */
  available: boolean;
  entitled: boolean;
  code: string | null;
  /** The server's own sentence where it sent one. Never empty. */
  message: string;
  reason: string | null;
  /**
   * The next step, WHEN THERE IS A COMMERCIAL ONE. `null` when the refusal is a deployment
   * state: there is nobody to contact about a flag, and printing an account team here
   * would send a customer who has already bought the pass to buy it again.
   */
  contact: string | null;
  /**
   * `true` when this gate came from a REFUSED request rather than from the free
   * inventory. Same contact path either way; the screen only uses it to acknowledge that
   * something was attempted, so a click does not appear to have done nothing.
   */
  fromRefusal: boolean;
  /**
   * THE CAPABILITY IS NOT DEPLOYED HERE — not "not bought".
   *
   * Keeps the two refusals distinguishable all the way to the markup. A screen that only
   * had `available: false` would render one sentence for both, and the sentence it would
   * pick is the sales one, because that is the one the entitlement refusal needs.
   */
  deploymentDisabled: boolean;
}

/** The inventory's `triage` block, read as a gate. Closed unless it says otherwise. */
export function portfolioGate(triage: PortfolioTriage | null): PortfolioGate {
  return {
    available: triage?.available === true,
    entitled: triage?.entitled === true,
    code: triage?.code ?? null,
    message: triage?.message || PORTFOLIO_GATE_FALLBACK_MESSAGE,
    reason: triage?.reason ?? null,
    contact: triage?.contact || PORTFOLIO_CONTACT_FALLBACK,
    fromRefusal: false,
    deploymentDisabled: false,
  };
}

/**
 * The gate for a `PORTFOLIO_DISABLED` refusal. NO CONTACT PATH, deliberately.
 *
 * Every other closed gate on this screen ends in "here is who to call", because the
 * refusal it describes is a purchase away from being resolved. This one is not: an
 * operator turns `PLAYGROUND_MIGRATION_PORTFOLIO` on, and no amount of talking to an
 * account team changes what a deployment has mounted. Sending the reader down the sales
 * path here wastes their time and misreports our own configuration as their bill.
 */
export function portfolioDisabledGate(triage: PortfolioTriage): PortfolioGate {
  return {
    available: false,
    entitled: triage.entitled === true,
    code: triage.code,
    message: triage.message || PORTFOLIO_DISABLED_FALLBACK_MESSAGE,
    reason: triage.reason,
    contact: null,
    fromRefusal: true,
    deploymentDisabled: true,
  };
}

/**
 * The gate the screen actually renders: the inventory's triage, UNLESS a paid call has
 * since been refused, in which case the server's answer wins.
 *
 * The refusal outranks the inventory deliberately. If the inventory said `available: true`
 * and the estimate call came back 403, the truth is the 403 — entitlement may have
 * lapsed, the inventory may be cached, or the client may simply have been wrong. In every
 * one of those cases the screen must stop offering the run and start showing the contact
 * path, which is what returning a closed gate here does.
 *
 * AND `PORTFOLIO_DISABLED` OUTRANKS THE 403 IN TURN, which is the ordering that matters:
 * a deployment that serves no passes cannot be bought into, so if both signals are somehow
 * present the honest one is the flag. Checked first for exactly that reason.
 */
export function resolvePortfolioGate(
  triage: PortfolioTriage | null,
  paidError: unknown
): PortfolioGate {
  const disabled = portfolioDisabledRefusal(paidError);
  if (disabled) return portfolioDisabledGate(disabled);
  const refusal = portfolioRefusal(paidError);
  if (refusal) return { ...portfolioGate(refusal), fromRefusal: true };
  return portfolioGate(triage);
}

export interface PortfolioMetric {
  label: string;
  /** Already formatted. `?` when the server sent no count — never `0`. */
  value: string;
  known: boolean;
}

export interface PortfolioUnaddressable {
  count: number;
  known: boolean;
  tone: CoverageTone;
  label: string;
  /** One sentence saying what this number means for the reader. */
  detail: string;
}

export interface PortfolioScale {
  /** `counts.workflow_rules`. Structurally cannot be `addressable` — see the file note. */
  headline: PortfolioMetric;
  /** Rendered beside the headline at EVERY value, zero included. Never disclosed away. */
  unaddressable: PortfolioUnaddressable;
  metrics: PortfolioMetric[];
  /** Everything that makes the numbers above less than a total. Never hidden. */
  caveats: string[];
}

/** A count that may not have arrived. `?` rather than a number nobody measured. */
export function countLabel(count: number): string {
  return count === PORTFOLIO_COUNT_UNKNOWN || count < 0 ? "?" : String(count);
}

const UNADDRESSABLE_CLEAR = "Every rule in this org can be addressed by a pass.";

const UNADDRESSABLE_SOME =
  "A pass cannot be built over these. They stay exactly as they are and are not part of anything the pass reports.";

const UNADDRESSABLE_UNKNOWN =
  "The server did not report how many rules a pass could not be built over. Absent is not zero — nothing below can be read as full coverage.";

export function portfolioScale(inventory: PortfolioInventory): PortfolioScale {
  const counts = inventory.counts;
  const unaddressableKnown = counts.unaddressable !== PORTFOLIO_COUNT_UNKNOWN;
  const totalKnown = counts.workflow_rules !== PORTFOLIO_COUNT_UNKNOWN;

  return {
    headline: {
      label: "workflow rules",
      value: countLabel(counts.workflow_rules),
      known: totalKnown,
    },
    unaddressable: {
      count: unaddressableKnown ? counts.unaddressable : 0,
      known: unaddressableKnown,
      // Caution, not alarm, and never green: rules a pass cannot reach are a fact about
      // the org rather than a failure of anything. Unknown is treated as caution too,
      // because the honest reading of an absent count is "we cannot say", not "none".
      tone: !unaddressableKnown || counts.unaddressable > 0 ? "warm" : "neutral",
      label: unaddressableKnown
        ? `${counts.unaddressable} unaddressable`
        : "unaddressable not reported",
      detail: !unaddressableKnown
        ? UNADDRESSABLE_UNKNOWN
        : counts.unaddressable > 0
          ? UNADDRESSABLE_SOME
          : UNADDRESSABLE_CLEAR,
    },
    metrics: [
      { label: "objects", value: countLabel(counts.objects), known: counts.objects >= 0 },
      { label: "profiles", value: countLabel(counts.profiles), known: counts.profiles >= 0 },
      {
        // Explicitly "of the total", so this number can never be mistaken for the total.
        label: "addressable by a pass",
        value: `${countLabel(counts.addressable)} of ${countLabel(counts.workflow_rules)}`,
        known: counts.addressable >= 0 && totalKnown,
      },
    ],
    caveats: portfolioCaveats(inventory),
  };
}

/**
 * Everything that makes the counts above less than a total.
 *
 * `complete: false` leads, because it invalidates every other number on the panel at
 * once: an inventory that stopped part-way reports floors, and a floor rendered next to
 * the word "rules" reads as a total to everyone who has not been told otherwise.
 */
export function portfolioCaveats(inventory: PortfolioInventory): string[] {
  const caveats: string[] = [];
  if (!inventory.complete) {
    caveats.push(
      `This inventory is incomplete — ${inventory.pages_read} page(s) were read and the listing did not finish. Every count here is a floor, not a total.`
    );
  }
  if (inventory.counts.workflow_rules === PORTFOLIO_COUNT_UNKNOWN) {
    caveats.push(
      "The server did not report a rule total, so the headline above is not a measurement."
    );
  }
  if (inventory.by_object.unreadable > 0) {
    caveats.push(
      `${inventory.by_object.unreadable} row(s) of the per-object breakdown could not be read and are missing from the table below. The totals above still include them.`
    );
  }
  return caveats;
}

/**
 * The `POST /passes` body, built from an estimate a human has just been shown — or a
 * refusal to build one.
 *
 * TWO THINGS ARE ENFORCED HERE AND NOWHERE ELSE ON THE CLIENT.
 *
 *  1. `accept_estimate` is the literal `true`, and this function is only reachable from
 *     the confirm step. The field means "a person read the estimate", so it cannot be
 *     defaulted, inferred, or set by the code path that merely fetched one.
 *
 *  2. `component_count` comes from the estimate's `addressable` — the rules a pass can
 *     actually be built over — and an UNKNOWN count refuses rather than sending a
 *     sentinel. `-1` on a paid create is the kind of value that gets interpreted
 *     somewhere downstream, and a refusal a human can read is cheaper than finding out
 *     where.
 */
export function passRequestFrom(
  estimate: PortfolioEstimate,
  title?: string
): { ok: true; body: PortfolioPassRequest } | { ok: false; reason: string } {
  if (estimate.counts.addressable === PORTFOLIO_COUNT_UNKNOWN || estimate.counts.addressable < 0) {
    return {
      ok: false,
      reason:
        "The estimate did not report how many rules are addressable, so there is no component count to create a pass with. Nothing was sent.",
    };
  }
  if (!estimate.scope) {
    return {
      ok: false,
      reason: "The estimate named no scope, so there is nothing to create a pass over. Nothing was sent.",
    };
  }
  return {
    ok: true,
    body: {
      scope: estimate.scope,
      component_count: estimate.counts.addressable,
      accept_estimate: true,
      ...(estimate.inventory_sha ? { inventory_sha: estimate.inventory_sha } : {}),
      ...(title ? { title } : {}),
    },
  };
}

/**
 * What the confirm screen leads with, and what it must refuse to imply.
 *
 * The estimate's field names are confirmed against the server. Three of them decide how
 * honest this screen is:
 *
 *  * `wall_clock_human` — how long the customer's DEPLOY QUEUE is occupied. Rendered
 *    verbatim; the server pre-formats it and a client that reformatted seconds would
 *    eventually disagree with the string the server sent.
 *
 *  * `per_deploy_source` — whether the per-deploy timing was `measured` on THIS org or is
 *    a `default`. A duration derived from a default is an order of magnitude, not a
 *    quote, and presenting it as though the org had been measured is the exact dishonesty
 *    this file exists to prevent. `wallClockMeasured` is false unless the server said
 *    `measured`, so an absent or unrecognised source reads as not-measured.
 *
 *  * `deploys_are_an_upper_bound` — the deploy count may be fewer in practice. Said
 *    beside the number rather than in a footnote, because it is the number a reader will
 *    multiply by anything they care about.
 *
 * Everything the server sends that is NOT surfaced above still renders through `rows`.
 * The field list is confirmed, not frozen, and a new key appearing in the estimate should
 * show up on the screen rather than vanish because this client had not heard of it.
 */
export interface PortfolioEstimateSummary {
  /** `wall_clock_human`, verbatim. `null` when the server sent none. */
  wallClock: string | null;
  /** `true` only when `per_deploy_source === "measured"`. Absent reads as false. */
  wallClockMeasured: boolean;
  timingSource: string | null;
  timingSamples: number | null;
  deploys: number | null;
  deploysUpperBound: boolean;
  components: number | null;
  apiCalls: number | null;
  concurrency: number | null;
  concurrencyReason: string | null;
  /** What the figures above must not be read as. Never behind a disclosure. */
  caveats: string[];
  /** Every remaining scalar the server sent, so a new field cannot go unrendered. */
  rows: Array<{ label: string; value: string }>;
}

/** Keys rendered explicitly above, and therefore not repeated in `rows`. */
const SURFACED_ESTIMATE_KEYS = new Set([
  "components",
  "deploys",
  "deploys_are_an_upper_bound",
  "api_calls",
  "wall_clock_seconds",
  "wall_clock_human",
  "per_deploy_source",
  "per_deploy_samples",
  "concurrency",
  "concurrency_reason",
]);

export function estimateSummary(estimate: PortfolioEstimate): PortfolioEstimateSummary {
  const raw = estimate.estimate;
  const wallClock = typeof raw.wall_clock_human === "string" ? raw.wall_clock_human : null;
  const timingSource = typeof raw.per_deploy_source === "string" ? raw.per_deploy_source : null;
  const measured = timingSource === "measured";
  const deploysUpperBound = raw.deploys_are_an_upper_bound === true;

  const caveats: string[] = [];
  if (wallClock && !measured) {
    caveats.push(
      `The per-deploy timing behind this duration was not measured on this org (per_deploy_source: ${timingSource ?? "not reported"}). Read it as an order of magnitude, not as a quote.`
    );
  }
  if (deploysUpperBound) {
    caveats.push(
      "The deploy count is an upper bound. The pass may need fewer, and the duration above scales with it."
    );
  }
  if (!wallClock) {
    caveats.push(
      "The server returned no wall-clock estimate, so how long this occupies the deploy queue is not known from this screen."
    );
  }

  const rows: Array<{ label: string; value: string }> = [];
  for (const [key, value] of Object.entries(raw)) {
    if (SURFACED_ESTIMATE_KEYS.has(key)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      rows.push({ label: humanize(key), value: String(value) });
    }
  }

  return {
    wallClock,
    wallClockMeasured: measured,
    timingSource,
    timingSamples: numberOrNull(raw.per_deploy_samples),
    deploys: numberOrNull(raw.deploys),
    deploysUpperBound,
    components: numberOrNull(raw.components),
    apiCalls: numberOrNull(raw.api_calls),
    concurrency: numberOrNull(raw.concurrency),
    concurrencyReason:
      typeof raw.concurrency_reason === "string" ? raw.concurrency_reason : null,
    caveats,
    rows,
  };
}
